import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from '../../../../services/notificationService';
import {
  useRoles,
  useTenantAppAssignments,
  useUserRoles,
  useAssignUserRole,
  useRevokeUserRole,
} from 'hooks/api';
import { SyutSelect } from 'components/ui';
import { lookupService, type StatusLookup } from 'api/services/lookupService';
import type { TenantUserResponse } from 'api/types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface TenantUserRolesModalProps {
  isOpen: boolean;
  /** The tenant user whose roles are being managed. */
  user: TenantUserResponse | null;
  /** Tenant currently selected on the screen — roles are assigned within it. */
  tenantId: string;
  /** Label for the tenant, shown in the header. */
  tenantLabel?: string;
  onClose: () => void;
  /** Fired after assignments change so the caller can refresh. */
  onSaved?: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (userName: string) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

const TenantUserRolesModal: React.FC<TenantUserRolesModalProps> = ({
  isOpen,
  user,
  tenantId,
  tenantLabel,
  onClose,
  onSaved,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  // Role assignments use the GLOBAL user id, not the tenant-user row id
  const globalUserId = user?.userId || null;

  const { data: allRoles, loading: rolesLoading } = useRoles();
  /** Applications assigned to this tenant — the only ones whose roles are relevant here. */
  const { data: tenantApps = [], loading: appsLoading } = useTenantAppAssignments(
    isOpen && tenantId ? { tenantIds: [tenantId], page: 1, pageSize: 500 } : undefined,
  );
  const [selectedAppId, setSelectedAppId] = useState('');
  const {
    data: assignedRoles,
    loading: assignedLoading,
    message: assignedMessage,
    refetch: refetchAssigned,
  } = useUserRoles(isOpen ? globalUserId : null, isOpen ? tenantId : null);
  const { assignRole } = useAssignUserRole();
  const { revokeRole } = useRevokeUserRole();

  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  /** Status the assignment is created with — resolved from the API, not hard-coded. */
  const [activeStatusId, setActiveStatusId] = useState<string>('');

  const initialSelectionRef = useRef<string>('');

  // ---- Assignment status (iam.tbl_status, general type) ----
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    lookupService.getStatuses('general').then((res) => {
      if (cancelled) return;
      const options = ((res.data as any)?.data || res.data || []) as StatusLookup[];
      const active = options.find(s => (s.code || '').toLowerCase() === 'active');
      setActiveStatusId(active?.id || '');
    }).catch(() => { /* surfaced on save if still missing */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  // ---- Seed the ticks from what the user already holds ----
  useEffect(() => {
    if (!isOpen) return;
    const ids = new Set(assignedRoles.map(a => String(a.roleId)));
    setSelectedRoleIds(ids);
    initialSelectionRef.current = JSON.stringify([...ids].sort());
  }, [isOpen, assignedRoles]);

  useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  /** Assignment id per role — needed to revoke. */
  const assignmentIdByRole = useMemo(() => {
    const map = new Map<string, string>();
    assignedRoles.forEach(a => map.set(String(a.roleId), String(a.id)));
    return map;
  }, [assignedRoles]);

  /** Applications assigned to this tenant, de-duplicated for the picker. */
  const applicationOptions = useMemo(() => {
    const seen = new Map<string, string>();
    tenantApps.forEach(a => {
      const id = String(a.appId || '');
      if (id && !seen.has(id)) seen.set(id, `${a.appCode} — ${a.appName}`);
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [tenantApps]);

  const appLabelById = useMemo(
    () => new Map(applicationOptions.map(o => [o.value, o.label])),
    [applicationOptions],
  );

  // Default to the tenant's first application so the list is never empty on open
  useEffect(() => {
    if (!isOpen) { setSelectedAppId(''); return; }
    if (!selectedAppId && applicationOptions.length > 0) {
      setSelectedAppId(applicationOptions[0].value);
    }
  }, [isOpen, applicationOptions, selectedAppId]);

  /** Roles belonging to the chosen application. */
  const appRoles = useMemo(
    () => (selectedAppId ? allRoles.filter(r => String(r.appId || '') === selectedAppId) : []),
    [allRoles, selectedAppId],
  );

  /**
   * Ids currently on screen. Save only ever assigns or revokes within this set,
   * so roles the user holds under other applications are never touched.
   */
  const visibleRoleIds = useMemo(() => new Set(appRoles.map(r => String(r.id))), [appRoles]);

  const visibleRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? appRoles.filter(r =>
        (r.code || '').toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q) ||
        (r.roleType || '').toLowerCase().includes(q))
      : appRoles;
    // Assigned roles first so the current state is obvious
    return [...rows].sort((a, b) => {
      const aSel = selectedRoleIds.has(String(a.id)) ? 0 : 1;
      const bSel = selectedRoleIds.has(String(b.id)) ? 0 : 1;
      return aSel - bSel || (a.code || '').localeCompare(b.code || '');
    });
  }, [appRoles, search, selectedRoleIds]);

  /** Assignments held under other applications — shown as a count, left alone on save. */
  const otherAppAssignments = useMemo(
    () => assignedRoles.filter(a => !visibleRoleIds.has(String(a.roleId))),
    [assignedRoles, visibleRoleIds],
  );

  const isDirty = useMemo(
    () => JSON.stringify([...selectedRoleIds].sort()) !== initialSelectionRef.current,
    [selectedRoleIds],
  );

  /**
   * Both sides are restricted to the roles of the selected application.
   * Without the visibleRoleIds guard, every role the user holds under another
   * application would count as "unticked" and be revoked on save.
   */
  const pendingChanges = useMemo(() => {
    const toAssign = [...selectedRoleIds].filter(
      id => visibleRoleIds.has(id) && !assignmentIdByRole.has(id),
    );
    const toRevoke = [...assignmentIdByRole.keys()].filter(
      id => visibleRoleIds.has(id) && !selectedRoleIds.has(id),
    );
    return { toAssign, toRevoke };
  }, [selectedRoleIds, assignmentIdByRole, visibleRoleIds]);

  const toggleRole = useCallback((roleId: string) => {
    setSelectedRoleIds(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }, []);

  // ---- Save: assign the newly ticked, revoke the unticked ----
  const handleConfirmSave = useCallback(async () => {
    setConfirmSaveOpen(false);
    if (!user || !globalUserId) return;

    if (pendingChanges.toAssign.length > 0 && !activeStatusId) {
      notificationService.toast.error('Could not resolve the assignment status. Please try again.');
      return;
    }

    setSaving(true);
    let assigned = 0;
    let revoked = 0;
    const failures: string[] = [];
    /** Saved in Tenant Hub but Keycloak did not fully sync — must not look like success. */
    const keycloakWarnings: string[] = [];

    for (const roleId of pendingChanges.toAssign) {
      const role = allRoles.find(r => String(r.id) === roleId);
      try {
        const result: any = await assignRole({
          userId: globalUserId,
          roleId,
          tenantId,
          assignmentType: 'direct',
          statusId: activeStatusId,
        });
        assigned++;
        // The API answers "…in database. Keycloak sync: <reason>" when the
        // Keycloak grant could not be made — report it instead of hiding it.
        const msg: string = result?.message || '';
        if (/keycloak sync:/i.test(msg)) {
          keycloakWarnings.push(`${role?.code || roleId}: ${msg.split(/keycloak sync:/i)[1].trim()}`);
        }
      } catch (err: any) {
        failures.push(`${role?.code || roleId} (${err?.response?.data?.message || err?.message || 'assign failed'})`);
      }
    }

    for (const roleId of pendingChanges.toRevoke) {
      const role = allRoles.find(r => String(r.id) === roleId);
      const assignmentId = assignmentIdByRole.get(roleId);
      if (!assignmentId) continue;
      try {
        await revokeRole(assignmentId, tenantId);
        revoked++;
      } catch (err: any) {
        failures.push(`${role?.code || roleId} (${err?.response?.data?.message || err?.message || 'revoke failed'})`);
      }
    }

    setSaving(false);
    await refetchAssigned();
    onSaved?.();

    if (failures.length === 0) {
      const parts: string[] = [];
      if (assigned) parts.push(`${assigned} assigned`);
      if (revoked) parts.push(`${revoked} revoked`);
      const summary = parts.length
        ? `Roles updated for "${user.userName}" — ${parts.join(', ')}.`
        : 'No changes to apply.';

      if (keycloakWarnings.length > 0) {
        // Saved here, but the user will not carry these roles in Keycloak
        notificationService.toast.warning(
          `${summary} Keycloak not updated for ${keycloakWarnings.slice(0, 2).join('; ')}` +
          (keycloakWarnings.length > 2 ? ` and ${keycloakWarnings.length - 2} more.` : '.'),
          8000,
        );
      } else {
        notificationService.toast.success(summary);
      }
      onClose();
    } else {
      notificationService.toast.warning(
        `${assigned + revoked} change(s) applied. Failed: ${failures.slice(0, 3).join('; ')}` +
        (failures.length > 3 ? ` and ${failures.length - 3} more.` : ''),
        8000,
      );
    }
  }, [user, globalUserId, tenantId, pendingChanges, activeStatusId, allRoles, assignmentIdByRole, assignRole, revokeRole, refetchAssigned, onSaved, onClose]);

  const handleCloseAttempt = useCallback(() => {
    if (isDirty) setConfirmDiscardOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !user) return;
    minimize({
      id: `${tabId}-roles-modal-${user.userName}`,
      title: `Roles - ${user.userName}`,
      moduleName,
      tabId,
      isDirty,
      onRestore: () => onRestoreView?.(user.userName),
    });
    onClose();
  }, [tabId, moduleName, user, minimize, isDirty, onRestoreView, onClose]);

  if (!isOpen || !user) return null;

  const loading = rolesLoading || assignedLoading;

  return (
    <>
      <div className="modal-backdrop" onClick={handleCloseAttempt} />
      <div className="modal-container modal-lg">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit',
          }}>
            <div style={{
              width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)',
              borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Updating roles...
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.lock size={20} />
            Manage Roles — {user.userName}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleCloseAttempt} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Roles apply within <strong>{tenantLabel || 'the selected tenant'}</strong>
            {user.email ? ` · ${user.email}` : ''}
          </div>

          {/* Application picker — only applications assigned to this tenant */}
          <div className="form-grid-2" style={{ marginBottom: '0.75rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <SyutSelect
                label="Application"
                options={applicationOptions}
                value={selectedAppId}
                onChange={(val: string | string[] | null) => { setSelectedAppId((val as string) || ''); setSearch(''); }}
                placeholder={appsLoading ? 'Loading applications...' : 'Select application'}
                isClearable={false}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search roles..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
          </div>

          {assignedMessage && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
              {assignedMessage}
            </div>
          )}

          {loading ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '1rem 0' }}>
              Loading roles...
            </div>
          ) : applicationOptions.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '1rem 0' }}>
              No applications are assigned to this tenant, so there are no roles to assign.
            </div>
          ) : !selectedAppId ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '1rem 0' }}>
              Select an application to see its roles.
            </div>
          ) : visibleRoles.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '1rem 0' }}>
              {search
                ? 'No roles match your search in this application.'
                : `No roles are defined for ${appLabelById.get(selectedAppId) || 'this application'}.`}
            </div>
          ) : (
            <div style={{
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              maxHeight: '320px',
              overflowY: 'auto',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: 'var(--bg-secondary)',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                position: 'sticky',
                top: 0,
              }}>
                <span style={{ width: '1.25rem', flexShrink: 0 }} />
                <span style={{ flex: 1 }}>Role</span>
                <span style={{ width: '110px', flexShrink: 0 }}>Type</span>
                <span style={{ width: '110px', flexShrink: 0, textAlign: 'center' }}>Assigned</span>
              </div>

              {visibleRoles.map(role => {
                const id = String(role.id);
                const checked = selectedRoleIds.has(id);
                return (
                  <div
                    key={id}
                    onClick={() => toggleRole(id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.375rem 0.75rem',
                      borderTop: '1px solid var(--border-color)',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      background: checked ? 'var(--bg-secondary)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRole(id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '1.25rem', flexShrink: 0, cursor: 'pointer' }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <strong>{role.code}</strong>
                      {role.name && role.name !== role.code ? (
                        <span style={{ color: 'var(--text-muted)' }}> — {role.name}</span>
                      ) : null}
                    </span>
                    <span style={{ width: '110px', flexShrink: 0, color: 'var(--text-secondary)' }}>
                      {role.roleType || '-'}
                    </span>
                    <span style={{ width: '110px', flexShrink: 0, textAlign: 'center' }}>
                      {assignmentIdByRole.has(id) && (
                        <span className="badge badge-active" style={{ fontSize: '0.7rem' }}>Current</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
          }}>
            <span>
              Assigned in this application:{' '}
              <strong>{[...selectedRoleIds].filter(id => visibleRoleIds.has(id)).length}</strong>
            </span>
            {pendingChanges.toAssign.length > 0 && <span>To add: {pendingChanges.toAssign.length}</span>}
            {pendingChanges.toRevoke.length > 0 && <span>To remove: {pendingChanges.toRevoke.length}</span>}
            {otherAppAssignments.length > 0 && (
              <span title={otherAppAssignments.map(a => a.roleCode).filter(Boolean).join(', ')}>
                {otherAppAssignments.length} role(s) held under other applications — not changed here
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCloseAttempt} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setConfirmSaveOpen(true)}
            disabled={saving || (pendingChanges.toAssign.length + pendingChanges.toRevoke.length === 0)}
          >
            Save Roles
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmSaveOpen}
        title="Update Roles"
        message={
          `Apply role changes for "${user.userName}"?` +
          (pendingChanges.toAssign.length ? ` Assign ${pendingChanges.toAssign.length}.` : '') +
          (pendingChanges.toRevoke.length ? ` Revoke ${pendingChanges.toRevoke.length}.` : '')
        }
        confirmLabel="Apply"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmSaveOpen(false)}
      />

      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Unsaved Changes"
        message="You have unsaved role changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setConfirmDiscardOpen(false); onClose(); }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
};

export default TenantUserRolesModal;
